import React, { act, useEffect, useRef, useState } from "react";
import { Player } from "@lottiefiles/react-lottie-player";
import { Link } from "gatsby";
import ReactMarkdown from "react-markdown";
import Container from "../Layout/container";
import rehypeRaw from "rehype-raw";
import { GatsbyImage, getImage } from "gatsby-plugin-image";

export const SquareTabs = ({ tabs, sectionColor }) => {
    // Estado para controlar el tab activo
    const [activeTab, setActiveTab] = useState(0);

    // Refs para detectar overflow y hacer scroll
    const tabsContainerRef = useRef(null);
    const tabsSquareRef = useRef(null);

    let tabsSquareClass = 'tabs-square';
    if (sectionColor) {
        tabsSquareClass += ` tabs-square--${sectionColor}`;
    }

    let tileClass = 'tile ';
    if (sectionColor) {
        tileClass += 'tile--white--ghost';
    } else {
        tileClass += 'tile__full-width tile--white--nb';
    }


    // Detectar si hay overflow horizontal y añadir/quitar la clase "tabs-responsive" SOLO en pantallas < 800px
    const checkOverflow = () => {
        if (!tabsSquareRef.current || !tabsContainerRef.current) return;

        const container = tabsSquareRef.current;
        const isOverflowing = container.scrollWidth > container.clientWidth;

        // Verificar tamaño de la ventana
        const isMobileViewport = window.innerWidth < 800;

        if (isMobileViewport || isOverflowing) {
            tabsContainerRef.current.classList.add('tabs-responsive');
        } else {
            // En cualquier otro caso quitamos la clase
            tabsContainerRef.current.classList.remove('tabs-responsive');
        }
    };

    // Al montar y redimensionar, ejecutamos la comprobación
    useEffect(() => {
        window.addEventListener('resize', checkOverflow);
        checkOverflow(); // Primera verificación al montar
        return () => {
            window.removeEventListener('resize', checkOverflow);
        };
    }, []);

    // Función para centrar
    const scrollToTab = (index, tabElement) => {
        if (!tabsContainerRef.current) return;

        const container = tabsContainerRef.current;

        const containerWidth = container.clientWidth;
        const scrollWidth = container.scrollWidth;

        // Si es la última pestaña, alineamos al final
        if (index === tabs.length - 1) {
            container.scrollTo({
                left: scrollWidth - containerWidth,
                behavior: 'smooth',
            });
            return;
        }

        // Calculamos posición para centrar la pestaña clicada
        const tabOffsetLeft = tabElement.offsetLeft;
        const tabWidth = tabElement.offsetWidth;
        const scrollLeft = tabOffsetLeft - containerWidth / 2 + tabWidth / 2;

        // Evitamos pasarnos de los límites
        const maxScroll = scrollWidth - containerWidth;
        const finalScrollLeft = Math.max(0, Math.min(scrollLeft, maxScroll));

        container.scrollTo({
            left: finalScrollLeft,
            behavior: 'smooth',
        });
    };

    const getIconClass = (icon) => {
        if (icon) {
            return `icon-${icon}`;
        }
        return 'icon-link';
    };

    const [isLoaded, setIsLoaded] = useState(false);
    useEffect(() => setIsLoaded(false), [activeTab]);

    return (
        <div className="container container--np">
            {/* "tabs-container" con ref para cambiar la clase según overflow */}
            <div className="tabs-container" ref={tabsContainerRef}>
                <div className={tabsSquareClass} ref={tabsSquareRef}>
                    {tabs.map((tab, index) => {
                        const iconClass = getIconClass(tab.proceso_onboarding_icon);
                        return (
                            <div
                                key={index}
                                className={`tabs-square__tab ${activeTab === index ? 'tabs-square__tab--active' : ''}`}
                                onClick={(e) => {
                                    setActiveTab(index);
                                    // Desplazamos la pestaña al centro (o al final si es la última)
                                    scrollToTab(index, e.currentTarget);
                                }}
                            >
                                <i className={`${iconClass} i-lg`}></i>
                                <h6>{tab.proceso_onboarding_name}</h6>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Mostrar únicamente el contenido del tab activo */}
            {tabs[activeTab] && (
                <div
                    key={`onboarding-${activeTab}-${tabs[activeTab].proceso_onboarding_item.id}`}
                    className={tileClass}
                >
                    <div className="tile__body">
                        <div className="grid grid-lg-2">
                            <div className="tile__body__column">
                                <h5>{tabs[activeTab].proceso_onboarding_item.titulo}</h5>
                                <ReactMarkdown children={tabs[activeTab].proceso_onboarding_item.descripcion} rehypePlugins={[rehypeRaw]} />
                                {tabs[activeTab].proceso_onboarding_item.porcentaje &&
                                    <div className="stat stat--boxed ghost">
                                        <div className="title-xl">
                                            {tabs[activeTab].proceso_onboarding_item.porcentaje}%
                                        </div>
                                        <span>
                                            <ReactMarkdown children={tabs[activeTab].proceso_onboarding_item.texto_porcentaje} rehypePlugins={[rehypeRaw]} />
                                        </span>
                                        {tabs[activeTab].proceso_onboarding_item.url_porcentaje && (
                                            <Link className="link--big" to={tabs[activeTab].proceso_onboarding_item.url_porcentaje}>
                                                {tabs[activeTab].proceso_onboarding_item.url_text}
                                                <i className="icon-arrow-right"></i>
                                            </Link>
                                        )}
                                    </div>
                                }
                            </div>
                            {tabs[activeTab].proceso_onboarding_item.imagen.localFile.publicURL ?
                                <div className="tile__body__column">
                                    <Player
                                        key={`player-${activeTab}`}
                                        src={tabs[activeTab].proceso_onboarding_item.imagen.localFile.publicURL}
                                        autoplay={true}
                                        keepLastFrame={true}
                                        loop={true}
                                        onEvent={(event) => {
                                            if (event === 'load') {
                                                setTimeout(() => {
                                                    setIsLoaded(true);
                                                }, 100);
                                            }
                                        }}
                                        className={`lottie-player ${isLoaded ? 'loaded' : ''}`}
                                    />
                                </div>
                                :
                                <div className="tile__body__column">
                                    <GatsbyImage image={getImage(tabs[activeTab].proceso_onboarding_item.imagen.localFile)} alt={tabs[activeTab].proceso_onboarding_item.imagen.alternativeText} />
                                </div>
                            }
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}